import {
  onNavSearchFirstTimeContentLoad,
  onNavSearchUpdateIsMyDesignContentLoading,
  onNavSearchUpdateSearchTerm,
  onNavSearchUpdateUserCreatedFolders,
  onNavSearchUpdateUserSearchPosters,
} from '@Components/nav-search/nav-search-slice';
import {
  getNavSearchUserFolderContent,
  getUserSearchForMyDesigns,
} from '@Components/template-search-content/components/template-search-my-design-content/template-search-my-design-content.types';
import type {NavFolderMeta, NavSearchContentAjaxResponse} from '@Components/nav-search/nav-search.types';
import {NAV_SEARCH_MOBILE_OPEN_EVENT} from '@Components/nav-search/nav-search.types';
import {hasAjaxRequestBeenMade, recordAjaxRequest, removeRecordedAjaxRequest} from '@Utils/ajax.util';
import {isUserLoggedIn} from '@Libraries/user.library';

const NAV_FOLDER_LAZY_LOAD_FETCH_LIMIT = 500;

export const isInGalleryDialog = (): boolean => {
  const isOpenInIframe = window.location !== window.parent.location;

  if (!isOpenInIframe) {
    return false;
  }
  return isGalleryDialogOpenInSameOrigin();
};

export const getGalleryDialogHostDocument = (): Document | null => {
  try {
    if (!isInGalleryDialog()) {
      return null;
    }
    return window.parent.document;
  } catch {
    return null;
  }
};

export const isMyStuffPage = (): boolean => {
  return window.location.pathname.includes('/posters/mine');
};

export const getNavSearchContent = (): Promise<NavSearchContentAjaxResponse> => {
  return window.PMW.readLocal('posters/getNavSearchContent');
};

export const setFirstTimeContent = async (isInitialContentLoading: boolean): Promise<void> => {
  const ajaxRequestID = 'search-content';

  if (!isInitialContentLoading || hasAjaxRequestBeenMade(ajaxRequestID)) {
    return;
  }
  setInitialSearchTermInGallerySearch();
  const dispatch = window.PMW.redux.store.dispatch;

  try {
    recordAjaxRequest(ajaxRequestID);
    const content: NavSearchContentAjaxResponse = await getNavSearchContent();
    removeRecordedAjaxRequest(ajaxRequestID);
    dispatch(onNavSearchFirstTimeContentLoad(content));

    if (content.folders.length === NAV_FOLDER_LAZY_LOAD_FETCH_LIMIT) {
      void fetchAllFoldersInBackground(NAV_FOLDER_LAZY_LOAD_FETCH_LIMIT, content.folders);
    }
  } catch (err) {
    console.error(err);
    console.log('Error preparing nav search bar');
  }
};

/**
 * Only called while the My Designs pill is showing: its results render nowhere else, so fetching them for
 * every Templates-pill search was a wasted request per typing pause.
 */
export const fetchMyDesignSearchResults = async (searchTerm: string): Promise<void> => {
  const dispatch = window.PMW.redux.store.dispatch;

  if (!isUserLoggedIn() || searchTerm.trim() === '') {
    dispatch(onNavSearchUpdateIsMyDesignContentLoading(false));
    return;
  }

  dispatch(onNavSearchUpdateIsMyDesignContentLoading(true));
  try {
    dispatch(onNavSearchUpdateUserSearchPosters(await getUserSearchForMyDesigns(searchTerm)));
  } catch {
    console.log('Error getting user search for my designs');
  } finally {
    dispatch(onNavSearchUpdateIsMyDesignContentLoading(false));
  }
};

const fetchAllFoldersInBackground = async (offset: number, accumulatedFolders: NavFolderMeta[]): Promise<void> => {
  const dispatch = window.PMW.redux.store.dispatch;

  try {
    const nextBatch = await getNavSearchUserFolderContent(offset);

    if (nextBatch.length === 0) {
      return;
    }
    const updatedFolders = accumulatedFolders.concat(nextBatch);
    dispatch(onNavSearchUpdateUserCreatedFolders(updatedFolders));

    if (nextBatch.length === NAV_FOLDER_LAZY_LOAD_FETCH_LIMIT) {
      void fetchAllFoldersInBackground(offset + NAV_FOLDER_LAZY_LOAD_FETCH_LIMIT, updatedFolders);
    }
  } catch {
    console.log('Error in fetching all user folders');
  }
};

export const setInitialSearchTermInGallerySearch = (): void => {
  const searchTerm = getSearchTermFromUrl();

  if (searchTerm) {
    window.PMW.redux.store.dispatch(onNavSearchUpdateSearchTerm(searchTerm));
  }
};

export const getSearchTermFromUrl = (): string => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('s') ?? '';
};

const isGalleryDialogOpenInSameOrigin = (): boolean => {
  try {
    return window.parent.document.getElementById('galleryDialogContainer') !== null;
  } catch {
    return false;
  }
};

export const isMyStuffQueryMatch = (searchTerm: string): boolean => {
  const normalized = searchTerm.toLowerCase().trim().replace(/\s+/g, '');
  const target = 'mystuff';

  return target.startsWith(normalized) && normalized.length >= 1;
};

export const isHighPriorityMyStuffMatch = (searchTerm: string): boolean => {
  const normalized = searchTerm.toLowerCase().trim().replace(/\s+/g, '');
  return isMyStuffQueryMatch(searchTerm) && normalized.length >= 3;
};

const MY_STUFF_HOME_URI = 'posters/mine?ifmss=1';

export const goToMyStuffHome = (): void => {
  const myStuffUrl = window.PMW.util.site_url(MY_STUFF_HOME_URI);

  if (typeof window.PMW.util?.isInGalleryDialog === 'function' && window.PMW.util.isInGalleryDialog()) {
    window.PMW.closeGalleryDialog(myStuffUrl, true);
    return;
  }

  window.location.href = myStuffUrl;
};

export const openNavSearchMobile = (): void => {
  document.dispatchEvent(new CustomEvent(NAV_SEARCH_MOBILE_OPEN_EVENT));
};
